import { useEffect, useCallback } from 'react';
import { useEditorStore, useExportStore } from '@/stores';

interface ShortcutHandler {
  key: string;
  ctrl?: boolean;
  meta?: boolean;
  shift?: boolean;
  handler: () => void;
  description: string;
}

export function useKeyboardShortcuts() {
  const { setActivePanel, setZoom, zoom, toggleGrid } = useEditorStore();
  const { isExporting } = useExportStore();

  const shortcuts: ShortcutHandler[] = [
    {
      key: 'e',
      ctrl: true,
      handler: () => !isExporting && setActivePanel('export'),
      description: 'Open export panel',
    },
    {
      key: 'f',
      ctrl: true,
      handler: () => setActivePanel('format'),
      description: 'Open format panel',
    },
    {
      key: 's',
      ctrl: true,
      handler: () => setActivePanel('template'),
      description: 'Open template panel',
    },
    {
      key: '=',
      ctrl: true,
      handler: () => setZoom(zoom + 0.1),
      description: 'Zoom in',
    },
    {
      key: '-',
      ctrl: true,
      handler: () => setZoom(zoom - 0.1),
      description: 'Zoom out',
    },
    {
      key: '0',
      ctrl: true,
      handler: () => setZoom(1),
      description: 'Reset zoom',
    },
    {
      key: 'g',
      ctrl: true,
      handler: () => toggleGrid(),
      description: 'Toggle grid',
    },
    {
      key: '1',
      handler: () => setActivePanel('format'),
      description: 'Format panel',
    },
    {
      key: '2',
      handler: () => setActivePanel('template'),
      description: 'Template panel',
    },
    {
      key: '3',
      handler: () => setActivePanel('layers'),
      description: 'Layers panel',
    },
    {
      key: '4',
      handler: () => setActivePanel('content'),
      description: 'Content panel',
    },
    {
      key: '5',
      handler: () => setActivePanel('animate'),
      description: 'Animate panel',
    },
    {
      key: '6',
      handler: () => setActivePanel('export'),
      description: 'Export panel',
    },
  ];

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      // Ignore if typing in input
      if (
        e.target instanceof HTMLInputElement ||
        e.target instanceof HTMLTextAreaElement ||
        e.target instanceof HTMLSelectElement
      ) {
        return;
      }

      for (const shortcut of shortcuts) {
        const ctrlOrMeta = shortcut.ctrl || shortcut.meta;
        const isCtrlOrMetaPressed = e.ctrlKey || e.metaKey;
        const shiftMatch = shortcut.shift ? e.shiftKey : !e.shiftKey;

        if (
          e.key.toLowerCase() === shortcut.key.toLowerCase() &&
          (ctrlOrMeta ? isCtrlOrMetaPressed : !isCtrlOrMetaPressed) &&
          shiftMatch
        ) {
          e.preventDefault();
          shortcut.handler();
          return;
        }
      }
    },
    [shortcuts]
  );

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  return shortcuts;
}

// Hook to show keyboard shortcuts help
export function getKeyboardShortcutsHelp(): { key: string; description: string }[] {
  return [
    { key: 'Ctrl/⌘ + E', description: 'Export' },
    { key: 'Ctrl/⌘ + F', description: 'Format panel' },
    { key: 'Ctrl/⌘ + S', description: 'Template panel' },
    { key: 'Ctrl/⌘ + G', description: 'Toggle grid' },
    { key: 'Ctrl/⌘ + +/-', description: 'Zoom in/out' },
    { key: 'Ctrl/⌘ + 0', description: 'Reset zoom' },
    { key: '1-6', description: 'Switch panels' },
  ];
}
